import React, { useState, useMemo, useEffect } from "react";
import { pdf } from "@react-pdf/renderer";
import html2canvas from "html2canvas";
import $, { parseHTML } from "jquery";
import JsPDF from "jspdf";
import moment from "moment";
import Papa from "papaparse";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { usePermissionGuardRedirect } from "../../../../hooks/usePermissionGuard";
import LetterheadIntakePDF from "../../../../LetterheadIntakePDF";
import { GetBrandSettingDetail } from "../../../../services/BrandServices";
import {
  getIntakeGradebook,
  getStudentBrand,
  updateGradeBookCousesScore,
} from "../../../../services/GradeSettingService";
import { options, twoDecimalRound } from "../../../../utils/commonFunction";
import { RenderLearningMethodGradebook } from "../../../../utils/CommonGroupingItem";
import { RenderQuizAttemptResultStatus } from "../../../../utils/CommonStatusItems";
import PermissionsGate from "../../../../utils/permissionGate";
import DataTableComponent from "../../../common/DataTableComponent";
import GradeBookSaveBar from "../../../common/GradeBookSaveBar";

/**
 * @description Extracts the students array from getIntakeGradebook API response.
 * 
 * The API returns students in two different shapes based on exportStatus:
 * - exportStatus "false": students are inside res.data.result.data (paginated)
 * - exportStatus "true":  res.data.result is directly the array of students
 * 
 * @param {Object} res - Axios response object from getIntakeGradebook
 * @returns {Array} Array of student objects
 */
const getGradebookStudents = (res) => {
  // Shape 1: res.data.result.data (exportStatus=false, paginated)
  if (Array.isArray(res?.data?.result?.data)) return res.data.result.data;
  // Shape 2: res.data.result directly array (exportStatus=true)
  if (Array.isArray(res?.data?.result)) return res.data.result;
  // Shape 3: res.result directly array (no axios data wrapper)
  if (Array.isArray(res?.result)) return res.result;
  return [];
};

/**
 * @description Normalizes an assessment object from assessmentArr into a
 * consistent shape for use in the StudentGrades table and PDF.
 * 
 * The getIntakeGradebook response uses "ass_name" and "assessment_id" as keys,
 * while the rest of the component expects "name" and "id". This mapper bridges
 * both naming conventions.
 * 
 * @param {Object} assessment - Raw assessment object from assessmentArr
 * @returns {Object} Normalized assessment with guaranteed "name" and "id" fields
 */
const mapAssessmentRow = (assessment) => ({
  ...assessment,
  name: assessment?.ass_name ?? assessment?.assessment_name ?? assessment?.name,
  id: assessment?.assessment_id ?? assessment?.id,
});

/**
 * @description Builds the "Final Course Grade" state object from a student and
 * course object returned by getIntakeGradebook.
 * 
 * This replaces the previous getGradeOfStudentViaCourse API call by deriving
 * the same data from the consolidated getIntakeGradebook response.
 * 
 * @param {Object} student - Student object from the assessmentArr result
 * @param {Object} course  - getCourse object from the API response
 * @returns {Object} Final grade data shaped for calculateGradeOfStudentViaCourse state
 */
const mapFinalGrade = (student, course) => ({
  allAss_ass_mark: student?.getAssessmentCalculate_sum_ass_mark,
  allAss_total_mark: student?.getAssessmentCalculate_sum_ass_total_mark,
  scoreRecieved: student?.scoreRecieved,
  pass_status: student?.pass_status,
  gradeText: student?.gradeText,
  grading_type: student?.grading_type,
  naturalPercantage: student?.naturalPercantage,
  condonement_mark: course?.condonement_mark,
  allow_condonement: course?.allow_condonement,
});

/**
 * @description Determines whether an assessment is still open (not yet closed)
 * by comparing close_date_time against current_date_time from the server.
 * 
 * Uses server-provided current_date_time instead of client Date.now() to avoid
 * timezone and clock-skew issues. Returns false if close_date_time is absent.
 * 
 * @param {Object} row - Assessment row object containing close_date_time and current_date_time
 * @returns {boolean} True if the assessment close time is in the future, false otherwise
 */
const isAssessmentStillOpen = (row) => {
  if (row?.close_date_time) {
    return moment.unix(row?.close_date_time) > moment.unix(new Date());
  }
  return false;
};

const StudentGrades = ({ courseName, stdName }) => {
  const state = localStorage.getItem("state");
  const brandVisuals = JSON.parse(state).brandVisuals.config;
  const { id, stdId } = useParams();
  let stdId_param = stdId.split("?")[0];
  const [search, setSearch] = useState("");
  const [assessmentList, setAssessmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateData, setUpdateData] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [saveGradesLoading, setSaveGradesLoading] = useState(false);
  const [percentageView, setPercentageView] = useState(false);
  const [gradingType, setGradingType] = useState();
  const [isCourseEdited, setIsCourseEdited] = useState(0);
  const [
    calculateGradeOfStudentViaCourse,
    setCalculateGradeOfStudentViaCourse,
  ] = useState({});
  const givenPermsisions = useSelector((state) => state.givenPermission);
  const [isChecked, setIsChecked] = useState(false);
  const [isShowFinal, setIsShowFinal] = useState(false);
  const [isExcludeGrade, setIsExcludeGrade] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfData, setPDFData] = useState([]);
  const [brandData, setBrandData] = useState({});
  const [studentDetails, setStudentDetails] = useState({});
  const [programmeDetails, setProgrammeDetails] = useState({});
  const [isSupplementary, setIsSupplementary] = useState(false);
  const [updateValueArr, setUpdateValueArr] = useState([]);
  const [brand_override, setBrandOverride] = useState("");
  const [isBrandOverride, setIsBrandOverride] = useState(false);
  const [disclaimer, setDisclaimer] = useState("");
  const [tableState, setTableState] = useState({
    page: 1,
    perPage: 10,
    sortKey: "name",
    sortOrder: "ASC",
  });

  usePermissionGuardRedirect(["casvview"]);

  // ─── Brand fetch ────────────────────────────────────────────────────────────
  useEffect(() => {
    const brandRequestData = {
      id: [stdId_param]
    }
    if(isBrandOverride && brand_override){
      GetBrandSettingDetail(brand_override)
      .then((brandResponse) => {
        setBrandData(brandResponse?.data?.brand_setting);
      })
      .catch((err) => console.error(err));
    }else{
      
      getStudentBrand(brandRequestData)
      .then((brandResponse) => {
        setBrandData(brandResponse?.data?.getBrandDetail);
      })
      .catch((err) => console.error(err));
    }
  }, [brand_override,isBrandOverride]);

  const handleSaveGrades = () => {
    setSaveGradesLoading(true);
    const values = {
      intake_id: id,
      student_id: stdId_param,
      assessment: updateValueArr
    };
    updateGradeBookCousesScore(values)
      .then((res) => {
        setSaveGradesLoading(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Updated Successfully",
        }).then((res) => setUpdateData(!updateData));
      })
      .catch((err) => {
        setSaveGradesLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `${err?.response?.data?.data}`,
        });
      });
  };
  
useEffect(() => {
  setLoading(true);

  const tableParams = {
    intake_id: id,
    key: tableState.sortKey,
    sort: tableState.sortOrder,
    exportStatus: "false",
    search: search,
    page: tableState.page,
    limit: tableState.perPage,
    isStudentRequest: 1,
    is_student: 1,
    students: [stdId_param],
  };

  const exportParams = {
    intake_id: id,
    exportStatus: "true",
    isStudentRequest: 1,
    is_student: 1,
    students: [stdId_param],
  };

  Promise.all([
    getIntakeGradebook(tableParams),
    getIntakeGradebook(exportParams),
  ])
    .then(([tableRes, exportRes]) => {

      // ── Table data (paginated — current page only) ──────────────────────
      const tableStudent = getGradebookStudents(tableRes)[0];
      const course = tableRes?.data?.getCourse;
      const assessments = (tableStudent?.assessmentArr || []).map(mapAssessmentRow);

      setStudentDetails(tableStudent);
      setBrandOverride(course?.override_brand);
      setIsBrandOverride(course?.override_sor_brand != 0);
      setProgrammeDetails({ ...course, name: course?.name ?? courseName });
      setIsCourseEdited(course?.allUpdatedCourse);
      setAssessmentList(assessments);
      // total from paginated shape; fallback to array length
      setTotalRows(tableRes?.data?.result?.total ?? assessments.length);

      // ── Export data (full list — all pages) ─────────────────────────────
      const exportStudent = getGradebookStudents(exportRes)[0];
      const exportCourse = exportRes?.data?.getCourse;
      const allAssessments = (exportStudent?.assessmentArr || []).map(mapAssessmentRow);

      setDisclaimer(exportCourse?.st_disclaimer);

      // isSupplementary — top level field from export response
      setIsSupplementary(exportRes?.data?.isSupplementaryForThisStudent ?? false);

      // ── Final grade — derived from full export data ──────────────────────
      const finalGrade = mapFinalGrade(exportStudent, exportCourse);
      setCalculateGradeOfStudentViaCourse(finalGrade);
      setGradingType(finalGrade?.grading_type);

      // ── PDF data — uses finalGrade local var (not stale state) ──────────
      if (finalGrade && allAssessments?.length) {
        const pdfDetails = allAssessments.map((assessment) => ({
          name: assessment?.name,
        weight: assessment?.grant_exemption
          ? "Exempted"
          : assessment?.ass_weighting,
          grade: assessment?.grant_exemption
            ? "Exempted"
            : percentageView
            ? assessment?.ass_percentage + "%"
            : assessment?.submited == "0" || isAssessmentStillOpen(assessment)
            ? "N/A"
            : assessment?.grading_type === 0
            ? assessment?.ass_percentage + "%"
            : assessment?.grading_type === 1
            ? options.find((option) => {
                const selectedValue =
                  assessment?.grade === 0
                    ? 0
                    : assessment?.grade > 0 && assessment?.grade <= 49
                    ? 49
                    : 100;
                return option?.value === selectedValue;
              })?.label ?? ""
            : assessment?.grading_type === 2
            ? assessment?.letterGrades?.length &&
              assessment?.letterGrades?.find((option) => option?.value === assessment?.grade)
              ? assessment?.letterGrades?.find((option) => option?.value === assessment?.grade).label
              : ""
            : assessment?.ass_mark,
          result: assessment?.grant_exemption
            ? "Exempted"
            : assessment?.submited == "0" || isAssessmentStillOpen(assessment)
            ? "N/A"
            : assessment?.pass_status
            ? assessment?.pass_status
            : "-",
          isExclude: assessment?.isExclude,
          final_status: finalGrade?.pass_status,
          isAssignedSickStudent : assessment?.isAssignedSickStudent,
          isSickForThisStudent : assessment?.isSickForThisStudent,
          isSupplementaryForThisStudent : assessment?.isSupplementaryForThisStudent,
          final_grade:
            finalGrade?.pass_status === "N/A"
              ? "N/A"
              : percentageView
              ? finalGrade?.scoreRecieved != null
                ? Math.round(parseFloat(finalGrade.scoreRecieved)).toString() + "%"
                : "N/A"
              : finalGrade?.grading_type === 3
              ? `${finalGrade?.allAss_ass_mark}/${finalGrade?.allAss_total_mark}`
              : finalGrade?.grading_type === 0
              ? finalGrade?.gradeText != null && finalGrade?.gradeText !== "N/A"
                ? typeof finalGrade.gradeText === "string" && finalGrade.gradeText.includes("%")
                  ? Math.round(parseFloat(finalGrade.gradeText.replace("%", ""))).toString() + "%"
                  : Math.round(parseFloat(finalGrade.gradeText)).toString() + "%"
                : "N/A"
              : finalGrade?.gradeText,
        }));
        setPDFData(pdfDetails);
      }

      setLoading(false);
    })
    .catch((err) => {
      console.error("gradebook fetch error:", err);
      setLoading(false);
    });
}, [id, stdId_param, tableState, search, updateData, percentageView]);

  // ─── PDF download ────────────────────────────────────────────────────────
  const handlepdfData = () => {
    setPdfDownloading(true);
    const swalPro = Swal.mixin({
      title: "Downloading PDF",
      text: "Please wait...",
      icon: "info",
      allowOutsideClick: false,
      timerProgressBar: true,
      onBeforeOpen: () => {
        Swal.showLoading();
      },
    });
    swalPro.fire();
    if (pdfData && brandData) {
      const modifiedSoR = pdfData
        .map((assessmentRecord) => ({
          ...assessmentRecord,
          final_grade: !isShowFinal ? "" : assessmentRecord?.final_grade,
          final_status: !isShowFinal ? "" : assessmentRecord?.final_status,
        }))
        .filter((assessmentRecord) => !isExcludeGrade || !assessmentRecord.isExclude);

      const uniqueId = "st_disclaimer_" + Math.random() * 100;
      let kjs = `<div id='${uniqueId}' style='max-width: fit-content;'>${disclaimer}</div>`;
      kjs = parseHTML(kjs);
      document.querySelector("body").appendChild(kjs[0]);
      let divHeight = document.getElementById(uniqueId).clientHeight;
      divHeight = divHeight < 670 ? divHeight : 670;
      let divMinHeight = Math.floor(document.getElementById(uniqueId).innerText.toString().length / 200) * 18;
      let divWidth = document.getElementById(uniqueId).clientWidth;
      divWidth = document.getElementById(uniqueId).innerText.toString().length * 5;

      html2canvas(document.getElementById(uniqueId))
        .then((canvas) => canvas.toDataURL())
        .then((noteImage) => {
          pdf(
            <LetterheadIntakePDF
              detailData={modifiedSoR}
              noteImage={disclaimer ? noteImage : null}
                divMinHeight = {divMinHeight}
                divHeight={divHeight+300}
              divWidth={divWidth > 565 ? 565 : divWidth}
              divOrgHeight={divHeight}
                divTopPosition = {1197 - divHeight - 130}
              studentAndCourseDetails={{
                course_name: programmeDetails?.name,
                Student_name: studentDetails.full_name,
                ID_number: studentDetails?.number,
                Student_number: studentDetails?.student_crm_id
                  ? studentDetails?.student_crm_id
                  : studentDetails?.student_num,
                start: programmeDetails?.start
                  ? programmeDetails.start?.toString().length == 10
                    ? moment.unix(programmeDetails?.start).format("DD MMMM YYYY")
                    : moment(programmeDetails?.start).format("DD MMMM YYYY")
                  : "",
                end: programmeDetails?.end
                  ? programmeDetails.end?.toString().length == 10
                    ? moment.unix(programmeDetails?.end).format("DD MMMM YYYY")
                    : moment(programmeDetails?.end).format("DD MMMM YYYY")
                  : "",
                intake:
                  programmeDetails?.number && programmeDetails?.year
                    ? `${programmeDetails?.number}-${programmeDetails?.year}`
                    : "",
                learning_method: programmeDetails?.type
                  ? RenderLearningMethodGradebook(programmeDetails?.type).text
                  : "",
              }}
              brandData={brandData}
            />
          )
              
            .toBlob()
            .then((response) => {
              const blobURL = window.URL.createObjectURL(response);
              const a = document.createElement("a");
              a.href = blobURL;
              a.download = `${
                studentDetails?.student_crm_id
                  ? studentDetails?.student_crm_id
                  : studentDetails?.student_num
          }_${studentDetails.full_name}_${
            programmeDetails?.name
          }_SOR_${brandVisuals?.brand_name || ""}_${moment(new Date()).format(
            "DD MM YY"
          )}.pdf`; // Set the desired filename for the download
          a.click();

          URL.revokeObjectURL(blobURL);
          setPdfDownloading(false);
          setIsChecked(false);
          setIsShowFinal(false);
          setIsExcludeGrade(false);
          swalPro.close();
          $("#downloadSoRModal").modal("hide")
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Download Successfully",
          });
        })
        .catch((err) => {
          setPdfDownloading(false);
          setIsChecked(false);
          setIsShowFinal(false);
          setIsExcludeGrade(false);
          swalPro.close();
          $("#downloadSoRModal").modal("hide")
          Swal.fire({
            icon: "error",
            title: "Error",
            text: err,
          });
        });
      }).catch(err => console.error(err))
    }
  };
  // ─── Exemption toggle ────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      name: "Name",
      selector: "name",
      sortField: "name",
      sortable: true,
      cell: (row) => {
        return (
          <span title={row.name} className={`overflow-ellipsis2 ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ?  "supplementry-pointer-events" : ""}`}>
            {row.name ? (
              <Link
                className={`as-text-blue curser feature-name ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}
                to={`/courseAdministration/coursesdetails/${id}/assessments/open/assigned/Details/${row.id}`}
              >
                <span className="textLimit100">{row.name}</span>
              </Link>
            ) : (
              "-"
            )}
          </span>
        );
      },
    },
    {
      name: "Weight",
      selector: "weight",
      sortField: "ass_weighting",
      sortable: true,
      cell: (row) => 
        row?.grading_type == "4" ?
          <span className="">N/A</span> :
        row.grant_exemption ? (
          <span className={`exempted-text ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
            Exempted <i className="fal fa-info-circle"></i>
          </span>
        ) : (row?.ass_type == 9 && !row.isAssignedSickStudent) ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) ? "supplementry-opacity" : ""}`}>N/A</span>
        ) : (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>{row.ass_weighting}</span>
        ),
    },
    {
      name: "Score Recieved",
      selector: "ass_mark",
      sortable: false,
      cell: (row) => (
        <div className="d-flex">
          {row?.grading_type == "4" ?
            <span className="">N/A</span> : 
            row.grant_exemption ? (
            <span className={`exempted-text ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
              Exempted <i className="fal fa-info-circle"></i>
            </span>
          ) : (row?.ass_type == 9 && !row.isAssignedSickStudent) ? (
            <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) ? "supplementry-opacity" : ""}`}>N/A</span>
          ) : (
            <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>{twoDecimalRound(row.ass_mark) + "/" + row.ass_total_mark}</span>
          )}
          {(!row.grant_exemption && row?.grading_type != "4") && (
            <div className={`d-flex ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
              {row?.assessmentMarkingStatus?.toString() === "notsubmitted" ||
              row?.assessmentMarkingStatus?.toString() === "NOT SUBMITTED" ? (
                <div
                  className="grade-info-ic grade-info-red ml-2"
                  title="Not Submitted"
                >
                  <i className="fal fa-file-times"></i>
                </div>
              ) : (
                ""
              )}
              {!row?.mark_edited || row?.mark_edited == "0" ? (
                ""
              ) : (
                <div
                  className="grade-info-ic grade-info-orange ml-2"
                  title="Mark Edited"
                >
                  <i className="fal fa-edit"></i>
                </div>
              )}
              {row?.assessmentStatus?.toString() === "OPEN" ? (
                <div
                  className="grade-info-ic grade-info-green ml-2"
                  title="Open/Extension"
                >
                  <i className="fal fa-calendar-day"></i>
                </div>
              ) : (
                ""
              )}
              {row?.assessmentStatus?.toString() === "PENDING" ? (
                <div
                  className="grade-info-ic grade-info-blue ml-2"
                  title="Upcoming Assessment"
                >
                  <i className="fal fa-calendar-alt"></i>
                </div>
              ) : (
                ""
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Range",
      selector: "ass_total_mark",
      sortable: false,
      cell: (row) => 
        row?.grading_type == "4" ?
          <span className="">N/A</span> :
        row.grant_exemption ? (
          <span className={`exempted-text ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
            Exempted <i className="fal fa-info-circle"></i>
          </span>
        ) : (row?.ass_type == 9 && !row.isAssignedSickStudent) ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) ? "supplementry-opacity" : ""}`}>N/A</span>
        ) : (
          <p className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
            {row.ass_total_mark ? <span>0 - {row.ass_total_mark}</span> : "N/A"}
          </p>
        ),
    },
    {
      name: "Grade",
      selector: "grade",
      sortable: false,
      cell: (row) => {
        return row?.grading_type == "4" ?
          <span className="">N/A</span> :
          row.grant_exemption ? (
          <span className={`exempted-text ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
            Exempted <i className="fal fa-info-circle"></i>
          </span>
        ) : (row?.ass_type == 9 && !row.isAssignedSickStudent) ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) ? "supplementry-opacity" : ""}`}>N/A</span>
        ) : percentageView ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>{row.ass_percentage + "%"}</span>
        ) : !row?.notify_assessed && row?.submited == "0" ||
          moment.unix(row?.close_date_time) > moment.unix(new Date()) ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>N/A</span>
        ) : (
          <>
            <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
            {row.grading_type == 0
              ? row.ass_percentage + "%"
              : (row?.notify_assessed && (row?.submited == 0 || row?.submited == "")) && row.grading_type == 1 ?
                options.find(option => option.value == "0").label
              : row.grading_type == 1
              ? options.find(option => {
                const selectedValue = row.grade == 0 ? 0 : row.grade > 0 && row.grade <= 49 ? 49 : 100;
                return option.value === selectedValue;
              })?.label ?? ""
              : row?.grading_type == 2
              ? row?.gradeText && row?.gradeText != "" ? !isNaN(Number(row.gradeText)) ? twoDecimalRound(row?.gradeText) : row.gradeText : ""
              : twoDecimalRound(row?.gradeText) + "/" + row?.ass_total_mark}
              </span>
          </>
        );
      },
    },
    {
      name: "Result",
      selector: "pass_status",
      sortable: false,
      cell: (row) => 
        row?.grading_type == "4" ?
          <span className="">N/A</span> :
        row.grant_exemption ? (
          <span className={`exempted-text ${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>
            Exempted <i className="fal fa-info-circle"></i>
          </span>
        ) : (row?.ass_type == 9 && !row.isAssignedSickStudent) ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) ? "supplementry-opacity" : ""}`}>N/A</span>
        ) : !row.notify_assessed && row?.submited == "0" ||
          moment.unix(row?.close_date_time) > moment.unix(new Date()) ? (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>N/A</span>
        ) : row.pass_status ? (
          RenderQuizAttemptResultStatus(row.pass_status).html
        ) : (
          <span className={`${(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent ? "supplementry-opacity" : ""}`}>-</span>
        ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="assessment-08 btn-dropdown-grp">
          <div className="as-buttons">
            {row.grant_exemption ? (
              <PermissionsGate
                scopes={["coursegge"]}
                errorProps={{ disabled: true }}
              >
                <button
                  className="btn btn-success rounded-circle"
                  title="Remove from Exemption"
                  disabled={(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent || (row?.ass_type == 9 && !row.isAssignedSickStudent) || (row?.ass_type == 8 && !row.isSupplementaryForThisStudent) || isSupplementary}
                  onClick={() => handleUpdateExemption(row.id, 0)}
                >
                  <i className="fal fa-user-plus"></i>
                </button>
              </PermissionsGate>
            ) : (
              <PermissionsGate
                scopes={["coursegge"]}
                errorProps={{ disabled: true }}
              >
                <button
                  className="btn btn-danger rounded-circle"
                  title="Grant Exemption"
                  disabled={(isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent || (row?.ass_type == 9 && !row.isAssignedSickStudent) || (row?.ass_type == 8 && !row.isSupplementaryForThisStudent) || isSupplementary}
                  onClick={() => {
                    if((isSupplementary && !row.isSupplementaryForThisStudent) || row.isSickForThisStudent || (row?.ass_type == 9 && !row.isAssignedSickStudent) || (row?.ass_type == 8 && !row.isSupplementaryForThisStudent) || isSupplementary){
                      return false;
                    }
                   return handleUpdateExemption(row.id, 1);
                  }}
                >
                  <i className="fal fa-user-times"></i>
                </button>
              </PermissionsGate>
            )}
          </div>
        </div>
      ),
    },
  ]);
  const handleUpdateExemption = (assId, val) => {
    const updatedAssessmentList = assessmentList.map((assessment) => {
      if (assessment.id === assId) {
        const arr = updateValueArr;
        let ifAssessmentExists = false;
        arr.forEach((item, index) => {
          if (item.assessment_id === assId) ifAssessmentExists = index;
        });
        if (ifAssessmentExists === false) {
          arr.push({ student_id: stdId_param, assessment_id: assId, grant_exemption: val });
        } else {
          arr[ifAssessmentExists] = { ...arr[ifAssessmentExists], grant_exemption: val };
        }
        setUpdateValueArr(() => [...arr]);
        setIsCourseEdited(true);
        return { ...assessment, grant_exemption: val };
      }
      return assessment;
    });
    setAssessmentList(() => [...updatedAssessmentList]);
  };

  // ─── Export ──────────────────────────────────────────────────────────────
  const exportData = (fileType, fileName) => {
    const header = ["Name", "Weight", "Score Recieved", "Range", "Grade", "Result", "Final Grade"];
    const apiData = {
      intake_id: id,
      key: tableState.sortKey,
      sort: tableState.sortOrder,
      exportStatus: "true",
      search: search,
      page: tableState.page,
      limit: tableState.perPage,
      isStudentRequest: 1,
      is_student: 1,
      students: [stdId_param],
    };
    getIntakeGradebook(apiData).then((res) => {
      let data = (getGradebookStudents(res)[0]?.assessmentArr || []).map(mapAssessmentRow);
      data.push({
        "Final Grade": percentageView
          ? calculateGradeOfStudentViaCourse?.scoreRecieved + "%"
          : calculateGradeOfStudentViaCourse.grading_type == 3 &&
            calculateGradeOfStudentViaCourse?.allAss_ass_mark &&
            calculateGradeOfStudentViaCourse?.allAss_total_mark
          ? calculateGradeOfStudentViaCourse?.allAss_ass_mark +
            "/" +
            calculateGradeOfStudentViaCourse?.allAss_total_mark
          : calculateGradeOfStudentViaCourse?.gradeText,
        "Final Result": calculateGradeOfStudentViaCourse?.pass_status
          ? RenderQuizAttemptResultStatus(calculateGradeOfStudentViaCourse?.pass_status).text
          : "N/A",
      });
      data = data?.map((row) => ({
        ...row,
        Name: row?.name ? row.name : row["Final Grade"] ? "Final Course Grade" : "-",
        Weight: row.grant_exemption
          ? "Exempted"
          : row?.ass_weighting
          ? row.ass_weighting
          : row["Final Grade"]
          ? ""
          : row.ass_weighting,
        "Score Recieved": row.grant_exemption
          ? "Exempted"
          : row.ass_mark && row.ass_total_mark
          ? `${row.ass_mark}/${row.ass_total_mark}`
          : row["Final Grade"]
          ? ""
          : `${row.ass_mark}/${row.ass_total_mark}`,
        Range: row.grant_exemption
          ? "Exempted"
          : row.ass_total_mark
          ? "0 - " + row.ass_total_mark
          : row["Final Grade"]
          ? ""
          : "0 - " + row.ass_total_mark,
        Grade: row.grant_exemption
          ? "Exempted"
          : row?.submited == "0" || isAssessmentStillOpen(row)
          ? "N/A"
          : row.grading_type == 2
          ? row.letterGrades?.find((option) => option.value == row.grade)?.label ?? ""
          : row.grading_type == 1
          ? options.find((option) => {
              const selectedValue =
                row.grade == 0 ? 0 : row.grade > 0 && row.grade < 49 ? 49 : 100;
              return option.value == selectedValue;
            })?.label ?? ""
          : row.grade
          ? row.grading_type == 0
            ? row.grade + "%"
            : row.ass_mark
          : row["Final Grade"]
          ? ""
          : "N/A",
        Result: row.grant_exemption
          ? "Exempted"
          : row?.submited == "0" || isAssessmentStillOpen(row)
          ? "N/A"
          : row?.pass_status
          ? row?.pass_status
          : row["Final Grade"]
          ? ""
          : row?.pass_status,
        "Final Grade": row["Final Grade"] ? `${row["Final Grade"]} ${row["Final Result"]}` : "",
      }));

      if (fileType === "csv") {
        const csvString = Papa.unparse({ fields: header, data });
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });
        const blobURL = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.download = fileName;
        anchor.href = blobURL;
        anchor.dataset.downloadurl = [
          "text/csv",
          anchor.download,
          anchor.href,
        ].join(":");
        anchor.click();

        // Remove URL.createObjectURL. The browser should not save the reference to the file.
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobURL);
        }, 1000);
      } else if (fileType === "xlsx") {
        const compatibleData = data.map((row) => {
          const obj = {};
          header.forEach((col) => { obj[col] = row[col]; });
          return obj;
        });
        let wb = XLSX.utils.book_new();
        let ws1 = XLSX.utils.json_to_sheet(compatibleData, {
          header,
        });
        XLSX.utils.book_append_sheet(wb, ws1, "React Table Data");
        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return false;
      }
      if (fileType === "pdf") {
        const compatibleData = data.map((row) => {
          return [
          row.Name,
          row.Weight,
          row["Score Recieved"],
          row.Range,
          row.Grade,
          row.Result,
          row["Final Grade"],
          ];
        });
        const doc = new JsPDF();
        doc.autoTable({
          head: [header],
          body: compatibleData,
          styles: {
            minCellHeight: 10,
            minCellWidth: 5,
            halign: "left",
            fontSize: 8,
          },
        });
        doc.save(`${fileName}.pdf`);

        return false;
      }
    });
  };

  const handleToggle = () => {
    setIsChecked(!isChecked);
  };

  const handleShowFinalToggle = () => {
    setIsShowFinal(!isShowFinal);
  };

  const handleExcludeFinal = () => {
    setIsExcludeGrade(!isExcludeGrade);
  };

  return (
    <>
      <DataTableComponent
        data={assessmentList}
        loading={loading}
        state={tableState}
        setState={setTableState}
        setSearch={setSearch}
        totalRows={totalRows}
        columns={columns.filter((item) => item)}
        exportFunction={exportData}
        exportFileName={`${courseName} - ${stdName} Grade Book`}
        filters={[]}
        tableButton={[
          <PermissionsGate scopes={["castadd"]} errorProps={{ disabled: true }}>
            <button
              className="btn btn-primary"
              onClick={() => setPercentageView(!percentageView)}
              title={percentageView ? "Standard View" : "Percentage View"}
            >
              <i className="fal fa-eye"></i>
              {percentageView ? "Standard View" : "Percentage View"}
            </button>
          </PermissionsGate>,
        ]}
      />
      <div className="my-tickets-info-list Tickets-main-wrap tickets-new-custom mt-4">
        <div className="l-o-c-t custom-table-div filter-search-icon card card-table-custom">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <i className="fal fa-chart-bar mr-2 font-weight-bold"></i>
              <span className="curser feature-name font-weight-bold">
                Final Course Grade
              </span>
            </div>
            {calculateGradeOfStudentViaCourse?.pass_status == "N/A" ? (
              <div>
                <span className="mr-3">N/A</span>
                 {
                  programmeDetails.subject_type == "shortcourse" || !programmeDetails.qualification ?
                  <button
                    className="btn btn-primary rounded-circle"
                    title="Download SoR PDF"
                    data-toggle="modal"
                    data-target="#downloadSoRModal"
                    onClick={() => {
                      setIsShowFinal(programmeDetails.show_final_results);
                      setIsExcludeGrade(programmeDetails.show_empty_grades);
                    }}
                  
                  >
                    {pdfDownloading ? (
                      <i className="fas fa-cog fa-spin download-SoR-icon"></i>
                    ) : (
                      <i className="fal fa-download download-SoR-icon"></i>
                    )}
                 </button> : ""
                  }
              </div>
            ) : (
              <div className="resultstatus_flex arrowhide">
                {percentageView ? (
                  <>
                    {(calculateGradeOfStudentViaCourse?.scoreRecieved &&
                      calculateGradeOfStudentViaCourse?.scoreRecieved !=
                        "N/A") ||
                    calculateGradeOfStudentViaCourse?.scoreRecieved === 0
                      ? typeof calculateGradeOfStudentViaCourse?.scoreRecieved ==
                          "string" &&
                        calculateGradeOfStudentViaCourse?.scoreRecieved.includes(
                          "%"
                        )
                        ? Math.round(
                            parseFloat(
                              calculateGradeOfStudentViaCourse?.scoreRecieved.replace(
                                "%",
                                ""
                              )
                            )
                          ).toString() + "%"
                        : typeof calculateGradeOfStudentViaCourse?.scoreRecieved ==
                            "string" &&
                          !calculateGradeOfStudentViaCourse?.scoreRecieved.includes(
                            "%"
                          )
                        ? Math.round(
                            parseFloat(
                              calculateGradeOfStudentViaCourse?.scoreRecieved
                            )
                          ).toString() + "%"
                        : typeof calculateGradeOfStudentViaCourse?.scoreRecieved ===
                          "number"
                        ? calculateGradeOfStudentViaCourse?.scoreRecieved + "%"
                        : calculateGradeOfStudentViaCourse?.scoreRecieved
                        ? calculateGradeOfStudentViaCourse?.scoreRecieved
                        : "N/A"
                      : "N/A"}
                    <p className="m-0 mr-2 ml-2">
                      {
                        RenderQuizAttemptResultStatus(
                          calculateGradeOfStudentViaCourse?.pass_status
                        ).html
                      }
                    </p>
                  </>
                ) : calculateGradeOfStudentViaCourse?.grading_type == 3 ? (
                  calculateGradeOfStudentViaCourse?.allAss_ass_mark >= 0 ? (
                    <>
                        <PermissionsGate
                          scopes={["cassgradeassedit"]}
                          errorProps={{ disabled: true }}
                        >
                        <input
                        className={
                          "form-control" + (false ? " is-invalid" : "")
                        }
                          type="number"
                          value={twoDecimalRound(calculateGradeOfStudentViaCourse?.allAss_ass_mark) ?? 0}
                          disabled
                        />
                      </PermissionsGate>
                      <span>{`/${calculateGradeOfStudentViaCourse?.allAss_total_mark}`}</span>
                      <p className="m-0 mr-2 ml-2">
                      {
                        RenderQuizAttemptResultStatus(
                          calculateGradeOfStudentViaCourse?.pass_status
                        ).html
                      }
                      </p>
                    </>
                  ) : (
                    "N/A"
                  )
                ) : calculateGradeOfStudentViaCourse?.grading_type == 0 ? (
                  <>
                    {(calculateGradeOfStudentViaCourse?.gradeText &&
                      calculateGradeOfStudentViaCourse?.gradeText != "N/A") ||
                    calculateGradeOfStudentViaCourse?.gradeText === 0
                      ? typeof calculateGradeOfStudentViaCourse?.gradeText ==
                          "string" &&
                        calculateGradeOfStudentViaCourse?.gradeText.includes(
                          "%"
                        )
                        ? Math.round(
                            parseFloat(
                              calculateGradeOfStudentViaCourse?.gradeText.replace(
                                "%",
                                ""
                              )
                            )
                          ).toString() + "%"
                        : typeof calculateGradeOfStudentViaCourse?.gradeText ==
                            "string" &&
                          !calculateGradeOfStudentViaCourse?.gradeText.includes(
                            "%"
                          )
                        ? Math.round(
                            parseFloat(
                              calculateGradeOfStudentViaCourse?.gradeText
                            )
                          ).toString() + "%"
                        : typeof calculateGradeOfStudentViaCourse?.gradeText ===
                          "number"
                        ? calculateGradeOfStudentViaCourse?.gradeText + "%"
                        : calculateGradeOfStudentViaCourse?.gradeText
                        ? calculateGradeOfStudentViaCourse?.gradeText
                        : "N/A"
                      : "N/A"}
                    <p className="m-0 mr-2 ml-2">
                      {
                        RenderQuizAttemptResultStatus(
                          calculateGradeOfStudentViaCourse?.pass_status
                        ).html
                      }
                    </p>
                  </>
                ) : (
                  <>
                    {calculateGradeOfStudentViaCourse?.gradeText}
                    <p className="m-0 mr-2 ml-2">
                      {
                        RenderQuizAttemptResultStatus(
                          calculateGradeOfStudentViaCourse?.pass_status
                        ).html
                      }
                    </p>
                  </>
                )}
                {
                   programmeDetails.subject_type == "shortcourse" || !programmeDetails.qualification ?
                  <button
                    className="btn btn-primary rounded-circle"
                    title="Download SoR PDF"
                    data-toggle="modal"
                    data-target="#downloadSoRModal"
                    onClick={() => {
                      setIsShowFinal(programmeDetails.show_final_results);
                      setIsExcludeGrade(programmeDetails.show_empty_grades);
                    }}
                  >
                    {pdfDownloading ? (
                      <i className="fas fa-cog fa-spin download-SoR-icon"></i>
                    ) : (
                      <i className="fal fa-download download-SoR-icon"></i>
                    )}
                 </button> : ""
                }
             
              </div>
            )}
          </div>
        </div>
        {assessmentList?.length && isCourseEdited ? (
          <GradeBookSaveBar
            handleClick={() => handleSaveGrades()}
            saveGradesLoading={saveGradesLoading}
          />
        ) : (
          <></>
        )}
        <div
          className="topic-add-modal modal fade"
          id="downloadSoRModal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="exampleModalCenterTitle"
          aria-hidden="true"
          data-backdrop="static"
        >
          <div
            className="modal-600 modal-dialog modal-dialog-centered modal-m"
            role="document"
          >
            <div className="modal-content modal-border-update sor">
              <div className="modal-body p-0">
                <div className="modal-header modal-header-custom">
                  <h5 className="modal-title">
                    <i className="fal fa-download"></i> Statement of Results
                    Download Options
                  </h5>
                  <button
                    type="button"
                    className="close"
                    data-dismiss="modal"
                    aria-label="Close"
                    data-toggle="modal"
                    data-target="#downloadSoRModal"
                    onClick={() => {
                      setIsChecked(false);
                      setIsShowFinal(false);
                      setIsExcludeGrade(false);
                    }}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="t p-17px">
                  <i className="fal fa-cog SoR-icon"></i>
                  <span className="SoR-settings"> Settings</span>
                  <div className="toogles-inline mt-3 d-flex align-items">
                    <div className="d-flex align-items mr-3">
                      <label className="mb-0">Show Final Results</label>
                      <div className="toggle-switch ml-2">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={isShowFinal}
                            onChange={handleShowFinalToggle}
                          />
                          <span className="slider slider-round"></span>
                        </label>
                      </div>
                    </div>
                    <div className="d-flex align-items mr-3">
                      <label className="mb-0">Exclude Empty Grades</label>
                      <div className="toggle-switch ml-2">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={isExcludeGrade}
                            onChange={handleExcludeFinal}
                          />
                          <span className="slider slider-round"></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group form-group-save-cancel mt-4">
                    <button
                      className="btn btn-save btn-success"
                      type="submit"
                      title="Save"
                      onClick={() => handlepdfData()}
                    >
                      <i className="fal fa-download"></i> Download SoR
                    </button>
                    <button
                      className="btn btn-close btn-danger"
                      type="button"
                      title="Cancel"
                    >
                      <i
                        className="fal fa-times"
                        onClick={() => {
                          setIsChecked(false);
                          setIsShowFinal(false);
                          setIsExcludeGrade(false);
                          $("#downloadSoRModal").modal("hide");
                        }}
                      ></i>{" "}
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentGrades;
